import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { customizationsAPI, formatCustomizationMoney } from '../../api/customizationsAPI';
import AuthenticatedReferenceImage from '../../components/customizations/AuthenticatedReferenceImage';
import { artistItemsAPI } from '../../api/artistItemsAPI';
import { negotiationsAPI } from '../../api/negotiationsAPI';
import { fetchCart } from '../../store/slices/cartSlice';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import BoutiqueBriefView from '../../components/customizations/BoutiqueBriefView';
import PaintingBriefView from '../../components/customizations/PaintingBriefView';
import { trackPaintingCustomization } from '../../utils/analytics';

const Page = styled.main`
  background: #f6f1e8;
  min-height: calc(100vh - 120px);
  padding: 2rem 1rem 5rem;
`;
const Container = styled.div`margin: 0 auto; max-width: ${(p) => p.$wide ? '1100px' : '780px'};`;
const Back = styled(Link)`color: #1b4332; display: inline-block; font-weight: 800; margin-bottom: 1.25rem;`;
const Title = styled.h1`color: #1b4332; font-size: clamp(1.9rem, 4vw, 2.6rem); margin: 0 0 0.65rem;`;
const Heading = styled.h2`color: #1e293b; font-size: 1.35rem; margin: 1.6rem 0 0.65rem;`;
const Body = styled.p`color: #475569; line-height: 1.65; margin: 0 0 1rem;`;
const Muted = styled.p`color: #64748b; font-size: 0.88rem; line-height: 1.5; margin: 0.35rem 0;`;
const Card = styled.section`
  background: #fff; border: 1px solid #e3ddd2; border-radius: 1rem;
  box-shadow: 0 8px 24px rgba(42, 51, 45, 0.05); padding: 1.15rem;
`;
const Stack = styled.div`display: grid; gap: 1rem;`;
const Grid = styled.div`
  display: grid; gap: 1rem; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
`;
const ChoiceGrid = styled.div`
  display: grid; gap: 0.75rem; grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
`;
const Choice = styled.button`
  background: ${(p) => p.$active ? '#edf6ef' : '#fff'};
  border: ${(p) => p.$active ? '2px solid #1b4332' : '1px solid #cbd5e1'};
  border-radius: 0.8rem; color: #1e293b; cursor: pointer; font-weight: 750;
  min-height: 54px; padding: 0.8rem; text-align: left;
`;
const Button = styled.button`
  background: ${(p) => p.$secondary ? '#fff' : '#1b4332'};
  border: 1.5px solid #1b4332; border-radius: 0.65rem; color: ${(p) => p.$secondary ? '#1b4332' : '#fff'};
  cursor: pointer; font: inherit; font-weight: 850; min-height: 46px; padding: 0.7rem 1rem;
  &:disabled { cursor: not-allowed; opacity: 0.55; }
`;
const ButtonLink = styled(Link)`
  align-items: center; background: ${(p) => p.$secondary ? '#fff' : '#1b4332'};
  border: 1.5px solid #1b4332; border-radius: 0.65rem; color: ${(p) => p.$secondary ? '#1b4332' : '#fff'};
  display: inline-flex; font-weight: 850; justify-content: center; min-height: 46px;
  padding: 0 1rem; text-decoration: none;
`;
const Actions = styled.div`display: flex; flex-wrap: wrap; gap: 0.7rem; margin-top: 1.25rem;`;
const Label = styled.label`color: #334155; display: grid; font-size: 0.9rem; font-weight: 800; gap: 0.4rem;`;
const Input = styled.input`
  background: #fff; border: 1px solid #cbd5e1; border-radius: 0.65rem; font: inherit;
  min-height: 46px; padding: 0.7rem 0.8rem; width: 100%;
`;
const Textarea = styled.textarea`
  background: #fff; border: 1px solid #cbd5e1; border-radius: 0.65rem; font: inherit;
  min-height: 125px; padding: 0.8rem; resize: vertical; width: 100%;
`;
const Progress = styled.div`background: #d9d5cc; border-radius: 999px; height: 7px; margin: 0.5rem 0 1.5rem; overflow: hidden;`;
const ProgressFill = styled.div`background: #1b4332; height: 100%; width: ${(p) => `${p.$value}%`};`;
const ErrorBanner = styled.div`
  background: #fee2e2; border-radius: 0.65rem; color: #991b1b; line-height: 1.5;
  margin: 1rem 0; padding: 0.8rem 1rem;
`;
const Notice = styled.div`
  background: #edf6ef; border-radius: 0.75rem; color: #29543a; line-height: 1.5; padding: 0.9rem 1rem;
`;
const Chips = styled.div`display: flex; flex-wrap: wrap; gap: 0.55rem;`;
const Chip = styled.button`
  background: ${(p) => p.$active ? '#1b4332' : '#fff'}; border: 1px solid #819088;
  border-radius: 999px; color: ${(p) => p.$active ? '#fff' : '#334155'}; cursor: pointer;
  font-weight: 750; padding: 0.5rem 0.85rem;
`;
const Swatch = styled.button`
  background: ${(p) => p.$color}; border: ${(p) => p.$active ? '4px solid #1b4332' : '1px solid #94a3b8'};
  border-radius: 50%; cursor: pointer; height: 42px; width: 42px;
`;
const ReferenceGrid = styled.div`display: grid; gap: 0.8rem; grid-template-columns: repeat(auto-fill, minmax(145px, 1fr));`;
const Reference = styled.div`background: #fff; border: 1px solid #e2e8f0; border-radius: 0.7rem; overflow: hidden;`;
const Portfolio = styled.div`display: grid; gap: 0.4rem; grid-template-columns: repeat(3, 1fr);`;
const PortfolioImage = styled.img`aspect-ratio: 1; border-radius: 0.55rem; object-fit: cover; width: 100%;`;
const ArtistLogo = styled.img`border-radius: 50%; height: 56px; object-fit: cover; width: 56px;`;
const Badge = styled.span`
  background: #e8f3ec; border-radius: 999px; color: #1b4332; display: inline-block;
  font-size: 0.8rem; font-weight: 850; padding: 0.4rem 0.7rem;
`;
const Counter = styled.div`color: #64748b; font-size: 0.82rem; margin-top: 0.35rem; text-align: right;`;

const PRESETS = [
  [20, 25], [40, 60], [60, 90], [100, 120], [100, 150], [120, 150], [120, 180],
];
const CREATIVE_CHOICES = [
  ['I know exactly what I want', 'exact'],
  ['I have an idea', 'idea'],
  ['Surprise me', 'surprise'],
];
const COLOR_CHOICES = [
  ['Specific colors', 'specific'],
  ["Artist's imagination", 'artist_imagination'],
];
const COLORS = ['#111111', '#ffffff', '#ef4444', '#f59e0b', '#eab308', '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899'];
const META_KEY = (id) => `flea:custom-painting:${id}`;
const statusLabel = {
  draft: 'Draft', submitted: 'Choose an artist', sent: 'Waiting for quote',
  quoted: 'Quote received', accepted: 'Added to cart', converted: 'Ordered',
  cancelled: 'Cancelled', expired: 'Expired',
};
const kindLabel = {
  painting: 'Galleries',
  boutique: 'Boutiques',
  designer: 'Designers',
};

const apiMessage = (error, fallback = 'Something went wrong. Try again.') => {
  const data = error?.response?.data;
  if (typeof data === 'string') {
    const text = data.trim();
    if (!text || text.startsWith('<')) return fallback;
    return text;
  }
  if (typeof data?.error === 'string') return data.error;
  if (typeof data?.detail === 'string') return data.detail;
  if (data && typeof data === 'object') {
    const first = Object.values(data).flat().find((value) => typeof value === 'string');
    if (first) return first;
  }
  return fallback;
};
const shopId = (request) => request?.selected_shop?.id || request?.selected_shop || null;
const shopName = (request) => request?.selected_shop?.brand_name || request?.selected_shop_name || request?.shop_name || (shopId(request) ? `Artist #${shopId(request)}` : 'Artist not selected');
const briefDirection = (value) => CREATIVE_CHOICES.find(([, api]) => api === value)?.[0] || String(value || '').replace(/_/g, ' ');
const quoteFor = (request) => request?.active_quote || request?.quotes?.find((q) => q.status === 'accepted') || request?.quotes?.[0];

export function BuyerCustomizationIntroPage() {
  const navigate = useNavigate();
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');
  const startingRef = useRef(false);
  const draftPromiseRef = useRef(null);

  // Warm the draft while the buyer reads the steps so Start is usually already ready.
  useEffect(() => {
    let cancelled = false;
    const promise = customizationsAPI.createDraft().catch((err) => {
      if (draftPromiseRef.current === promise) draftPromiseRef.current = null;
      throw err;
    });
    draftPromiseRef.current = promise;
    promise.catch(() => { if (!cancelled) {/* leave Start to retry */} });
    return () => { cancelled = true; };
  }, []);

  const start = async () => {
    if (startingRef.current) return;
    startingRef.current = true;
    setStarting(true);
    setError('');
    try {
      const draft = await (draftPromiseRef.current || customizationsAPI.createDraft());
      draftPromiseRef.current = Promise.resolve(draft);
      trackPaintingCustomization('painting_request_started', {
        request_id: draft.id,
        state: draft.state,
      });
      navigate(`/custom-painting/${draft.id}/step/1`, { replace: true });
    } catch (err) {
      draftPromiseRef.current = null;
      setError(apiMessage(err));
      startingRef.current = false;
      setStarting(false);
    }
  };
  return (
    <Page><Container>
      <Back to="/artists">← Back to Galleries</Back>
      <Title>Create a custom painting</Title>
      <Body>Tell us about the painting you want, and we’ll show you artists who can create it. You’ll choose the artist you like after describing your idea.</Body>
      <Stack>
        {[
          ['1', 'Describe your painting', 'Share the size, vision, colors, references, and budget.'],
          ['2', 'Choose your artist', 'Explore artists matched to your description.'],
          ['3', 'Get a quote', 'Ask one artist for a price and delivery estimate.'],
        ].map(([number, title, body]) => <Card key={number}><Badge>{number}</Badge><Heading>{title}</Heading><Muted>{body}</Muted></Card>)}
      </Stack>
      <Notice style={{ marginTop: '1rem' }}>You won’t pay until you accept an artist’s quote.</Notice>
      {error && <ErrorBanner>{error}</ErrorBanner>}
      <Actions><Button onClick={start} disabled={starting}>{starting ? 'Starting…' : 'Start'}</Button></Actions>
    </Container></Page>
  );
}

export function BuyerCustomizationWizardPage() {
  const { requestId, step: routeStep } = useParams();
  const navigate = useNavigate();
  const step = Math.min(6, Math.max(1, Number(routeStep) || 1));
  const [request, setRequest] = useState(null);
  const [form, setForm] = useState({
    width: '', height: '', creative: '', description: '', colorMode: '', colors: [],
    subjects: '', requestedText: '', special: '', budgetMin: '', budgetMax: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef(null);
  const set = (key, value) => { setForm((current) => ({ ...current, [key]: value })); setDirty(true); };

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const value = await customizationsAPI.getBuyerRequest(requestId);
      if (value.state !== 'draft') {
        navigate(`/custom-painting/${value.id}/artists`, { replace: true });
        return;
      }
      const meta = JSON.parse(localStorage.getItem(META_KEY(value.id)) || '{}');
      const brief = value.painting_brief || {};
      setRequest(value);
      setForm({
        width: String(brief.width_cm || ''), height: String(brief.height_cm || ''),
        creative: brief.creative_direction || meta.creative || '',
        description: brief.description || '', colorMode: brief.color_preference || meta.colorMode || '',
        colors: brief.selected_colors || [], subjects: brief.objects_or_subjects || '',
        requestedText: brief.requested_text || '', special: value.special_requests || '',
        budgetMin: value.budget_min_cents ? String(value.budget_min_cents / 100) : '',
        budgetMax: value.budget_max_cents ? String(value.budget_max_cents / 100) : '',
      });
      setDirty(false);
    } catch (err) { setError(apiMessage(err, 'We couldn’t load your draft.')); }
    finally { setLoading(false); }
  }, [navigate, requestId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const warn = (event) => { if (dirty) { event.preventDefault(); event.returnValue = ''; } };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const validation = () => {
    if (step === 1 && (!(Number(form.width) > 0) || !(Number(form.height) > 0))) return 'Choose a size or enter both width and height using numbers greater than 0.';
    if (step === 2 && !form.creative) return 'Choose a creative direction.';
    if (step === 2 && !form.description.trim()) return 'Describe the painting you have in mind.';
    if (step === 3 && !form.colorMode) return 'Choose a color direction.';
    if (step === 3 && form.colorMode === 'specific' && !form.colors.length) return 'Add at least one color.';
    if (step === 3 && !form.subjects.trim() && !form.description.trim()) return 'Tell us what the painting should include.';
    if (step === 5 && form.special.length > 1024) return 'Special requests can’t exceed 1,024 characters.';
    if (step === 5 && !(Number(form.budgetMin) > 0)) return 'Enter your minimum budget.';
    if (step === 5 && !(Number(form.budgetMax) > 0)) return 'Enter your maximum budget.';
    if (step === 5 && Number(form.budgetMax) < Number(form.budgetMin)) return 'Maximum budget must be at least the minimum.';
    return '';
  };
  const payload = () => ({
    special_requests: form.special,
    budget_min_cents: form.budgetMin ? Math.round(Number(form.budgetMin) * 100) : null,
    budget_max_cents: form.budgetMax ? Math.round(Number(form.budgetMax) * 100) : null,
    painting_brief: {
      width_cm: form.width ? Number(form.width) : null,
      height_cm: form.height ? Number(form.height) : null,
      creative_direction: form.creative,
      description: form.description,
      color_preference: form.colorMode,
      selected_colors: form.colorMode === 'specific' ? form.colors : [],
      objects_or_subjects: form.subjects.trim() || form.description.trim(),
      requested_text: form.requestedText,
    },
  });
  const save = async (nextStep, exit = false) => {
    const issue = validation();
    if (issue) { setError(issue); return; }
    setSaving(true); setError('');
    try {
      const updated = await customizationsAPI.updateDraft(request.id, payload());
      localStorage.setItem(META_KEY(request.id), JSON.stringify({ lastStep: nextStep || step }));
      setRequest(updated); setDirty(false);
      if (exit) { toast.success('Saved'); navigate('/customization-requests'); }
      else if (nextStep) navigate(`/custom-painting/${request.id}/step/${nextStep}`);
    } catch (err) { setError(apiMessage(err)); }
    finally { setSaving(false); }
  };
  const upload = async (event) => {
    const selected = Array.from(event.target.files || []);
    event.target.value = '';
    const slots = 10 - (request?.references?.length || 0);
    if (!selected.length || slots <= 0) return;
    if (selected.some((file) => !file.type.startsWith('image/'))) { setError('Choose an image file.'); return; }
    if (selected.some((file) => file.size > 10 * 1024 * 1024)) { setError('Image must be 10 MB or smaller.'); return; }
    setUploading(true); setError('');
    try {
      const uploaded = await customizationsAPI.uploadReferences(request.id, selected.slice(0, slots));
      const refreshed = await customizationsAPI.getBuyerRequest(request.id);
      setRequest(refreshed);
      trackPaintingCustomization('painting_reference_uploaded', {
        request_id: request.id,
        uploaded_count: uploaded.length,
        reference_count: refreshed.references.length,
      });
    } catch (err) { setError(apiMessage(err, 'Upload failed. Try again.')); }
    finally { setUploading(false); }
  };
  const removeReference = async (id) => {
    setError('');
    try {
      await customizationsAPI.deleteReference(request.id, id);
      setRequest((current) => ({ ...current, references: current.references.filter((reference) => reference.id !== id) }));
    } catch (err) { setError(apiMessage(err)); }
  };
  const submit = async () => {
    setSaving(true); setError('');
    try {
      await customizationsAPI.updateDraft(request.id, payload());
      const completed = await customizationsAPI.submitDraft(request.id);
      trackPaintingCustomization('painting_request_completed', {
        request_id: request.id,
        width_cm: Number(completed.painting_brief.width_cm),
        height_cm: Number(completed.painting_brief.height_cm),
        creative_direction: completed.painting_brief.creative_direction,
        color_preference: completed.painting_brief.color_preference,
        reference_count: completed.references.length,
        budget_min_cents: completed.budget_min_cents,
        budget_max_cents: completed.budget_max_cents,
        state: completed.state,
      });
      localStorage.removeItem(META_KEY(request.id));
      navigate(`/custom-painting/${request.id}/artists`, { replace: true });
    } catch (err) { setError(apiMessage(err)); }
    finally { setSaving(false); }
  };

  if (loading) return <Page><LoadingSpinner text="Loading your draft…" /></Page>;
  if (!request) return <Page><Container><ErrorBanner>{error || 'We couldn’t load your draft.'}</ErrorBanner><Actions><Button onClick={load}>Try again</Button><ButtonLink to="/artists" $secondary>Back to Galleries</ButtonLink></Actions></Container></Page>;
  const refs = request.references || [];
  return (
    <Page><Container>
      <Back to="/artists">← Back to Galleries</Back>
      <Muted>YOUR CUSTOM PAINTING</Muted><strong>Step {step} of 6</strong>
      <Progress><ProgressFill $value={(step / 6) * 100} /></Progress>
      {step === 1 && <>
        <Title>What size should it be?</Title><Body>Choose a canvas size or enter your own. Dimensions are in centimeters.</Body>
        <ChoiceGrid>
          {PRESETS.map(([width, height]) => <Choice type="button" key={`${width}x${height}`} $active={Number(form.width) === width && Number(form.height) === height} onClick={() => { set('width', String(width)); set('height', String(height)); }}>{width} × {height} cm</Choice>)}
          <Choice type="button" $active={!PRESETS.some(([w, h]) => Number(form.width) === w && Number(form.height) === h)}>Arbitrary size</Choice>
        </ChoiceGrid>
        <Heading>Arbitrary size</Heading><Grid><Label>Width (cm)<Input type="number" min="0.01" value={form.width} onChange={(e) => set('width', e.target.value)} /></Label><Label>Height (cm)<Input type="number" min="0.01" value={form.height} onChange={(e) => set('height', e.target.value)} /></Label></Grid>
        <Muted>Enter the finished canvas dimensions.</Muted>
      </>}
      {step === 2 && <>
        <Title>What do you have in mind?</Title><Body>Choose the option that best describes your creative direction.</Body>
        <ChoiceGrid>{CREATIVE_CHOICES.map(([label, value]) => <Choice type="button" key={value} $active={form.creative === value} onClick={() => set('creative', value)}>{label}</Choice>)}</ChoiceGrid>
        <Heading>Describe your idea</Heading><Textarea maxLength={4000} value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="For example: A calm view of Alexandria at sunset, with the sea as the focus…" />
        <Muted>Describe the scene, mood, and details that matter most.</Muted>
      </>}
      {step === 3 && <>
        <Title>Choose colors and details</Title><Heading>Color direction</Heading>
        <ChoiceGrid>{COLOR_CHOICES.map(([label, value]) => <Choice type="button" key={value} $active={form.colorMode === value} onClick={() => set('colorMode', value)}>{label}</Choice>)}</ChoiceGrid>
        {form.colorMode === 'specific' && <><Heading>Colors</Heading><Chips>{COLORS.map((color) => <Swatch aria-label={`${form.colors.includes(color) ? 'Remove' : 'Add'} ${color}`} type="button" key={color} $color={color} $active={form.colors.includes(color)} onClick={() => set('colors', form.colors.includes(color) ? form.colors.filter((entry) => entry !== color) : form.colors.length < 8 ? [...form.colors, color] : form.colors)} />)}</Chips><Muted>Choose up to 8 colors.</Muted></>}
        <Heading>Objects or subjects to include</Heading><Textarea value={form.subjects} onChange={(e) => set('subjects', e.target.value)} placeholder="For example: two people, a cat, palm trees" />
        <Heading>Text to include (optional)</Heading><Input value={form.requestedText} onChange={(e) => set('requestedText', e.target.value)} placeholder="Enter the exact words, names, or date" />
      </>}
      {step === 4 && <>
        <Title>Add reference images</Title><Body>Upload images that help explain the subject, composition, colors, or mood.</Body>
        <Notice>Only you and the artist you contact can see these images.</Notice>
        <ReferenceGrid>{refs.map((reference) => <Reference key={reference.id}><AuthenticatedReferenceImage url={reference.url} alt={reference.original_filename || 'Painting reference'} aspect="1" /><Actions style={{ margin: '0.5rem' }}><Button type="button" $secondary onClick={() => window.open(reference.url, '_blank', 'noopener,noreferrer')} disabled={!reference.url}>View</Button><Button type="button" $secondary onClick={() => removeReference(reference.id)}>Delete</Button></Actions></Reference>)}</ReferenceGrid>
        <Muted>{refs.length} of 10 images</Muted>
        <input ref={fileRef} hidden type="file" accept="image/*" multiple onChange={upload} />
        <Actions><Button type="button" onClick={() => fileRef.current?.click()} disabled={uploading || refs.length >= 10}>{uploading ? 'Uploading…' : 'Add images'}</Button></Actions>
        <Muted>{refs.length >= 10 ? 'You’ve added the maximum of 10 images.' : 'Optional. Up to 10 images, 10 MB each.'}</Muted>
      </>}
      {step === 5 && <>
        <Title>Add the final details</Title>
        <Heading>Special requests (optional)</Heading><Textarea maxLength={1024} value={form.special} onChange={(e) => set('special', e.target.value)} placeholder="Share any deadline, framing preference, or detail the artist should know…" /><Counter>{form.special.length}/1,024</Counter>
        <Muted>Keep this to 1,024 characters or fewer. You can discuss details with the artist later.</Muted>
        <Heading>Your budget</Heading><Body>Set a range for the painting price. Delivery and any existing checkout fees are shown at checkout.</Body>
        <Grid><Label>Minimum budget<Input type="number" min="0.01" value={form.budgetMin} onChange={(e) => set('budgetMin', e.target.value)} /></Label><Label>Maximum budget<Input type="number" min="0.01" value={form.budgetMax} onChange={(e) => set('budgetMax', e.target.value)} /></Label></Grid>
        <Muted>Artists whose stated starting price is above your maximum won’t be shown. Artists marked “To be discussed” can still appear.</Muted>
      </>}
      {step === 6 && <>
        <Title>Review your painting brief</Title><Body>Check the details before we find artists for you.</Body>
        <PaintingBriefView request={{ ...request, ...payload(), painting_brief: payload().painting_brief }} />
        <Actions>{[['Size', 1], ['Vision', 2], ['Palette and details', 3], ['References', 4], ['Final touches', 5]].map(([label, target]) => <Button type="button" $secondary key={label} onClick={() => navigate(`/custom-painting/${request.id}/step/${target}`)}>Edit {label}</Button>)}</Actions>
        <Notice style={{ marginTop: '1rem' }}>You’ll choose an artist next. Sending the brief does not commit you to buy.</Notice>
      </>}
      {error && <ErrorBanner>{error}</ErrorBanner>}
      <Actions>
        {step < 6 ? <Button type="button" onClick={() => save(step + 1)} disabled={saving || uploading}>{saving ? 'Saving…' : step === 4 && !refs.length ? 'Continue without images' : 'Continue'}</Button> : <Button type="button" onClick={submit} disabled={saving}>{saving ? 'Finding artists…' : 'Find artists'}</Button>}
        <Button type="button" $secondary onClick={() => save(null, true)} disabled={saving || uploading}>Save and exit</Button>
        {step > 1 && <Button type="button" $secondary onClick={() => navigate(`/custom-painting/${request.id}/step/${step - 1}`)}>Back</Button>}
      </Actions>
    </Container></Page>
  );
}

export function BuyerCustomizationRecommendationsPage() {
  const { requestId } = useParams();
  const [artists, setArtists] = useState([]);
  const [fallback, setFallback] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const response = await customizationsAPI.getRecommendations(requestId);
      const results = response.results || [];
      setArtists(results); setFallback(Boolean(response.fallback));
      trackPaintingCustomization('painting_artists_viewed', {
        request_id: Number(requestId),
        artist_count: results.length,
        fallback: Boolean(response.fallback),
      });
    } catch (err) { setError(apiMessage(err, 'We couldn’t load artists.')); }
    finally { setLoading(false); }
  }, [requestId]);
  useEffect(() => { load(); }, [load]);
  if (loading) return <Page><LoadingSpinner text="Finding artists…" /></Page>;
  return (
    <Page><Container $wide>
      <Back to={`/custom-painting/${requestId}/step/6`}>← Back to Review</Back>
      <Title>Choose an artist</Title><Body>These artists accept custom painting requests and match the details you shared.</Body>
      {fallback && artists.length > 0 && <Notice><strong>No exact matches yet</strong><br />Here are other artists who accept custom painting requests. Review their work and choose anyone you like.</Notice>}
      {error && <><ErrorBanner>{error}</ErrorBanner><Actions><Button onClick={load}>Try again</Button><ButtonLink to={`/custom-painting/${requestId}/step/6`} $secondary>Edit brief</ButtonLink></Actions></>}
      {!error && !artists.length && <Card><Heading>No artists are accepting customization requests right now.</Heading><Muted>Your brief is saved. Check back later or update it.</Muted><Actions><ButtonLink to={`/custom-painting/${requestId}/step/6`}>Edit brief</ButtonLink><ButtonLink to="/artists" $secondary>Back to Galleries</ButtonLink></Actions></Card>}
      <Grid>{artists.map((artist) => <Card key={artist.shop_id}>
        {artist.logo_url && <ArtistLogo src={artist.logo_url} alt="" />}
        <Heading style={{ marginTop: 0 }}>{artist.brand_name}</Heading>
        <strong>{artist.customization_min_price_cents ? `Starting from ${formatCustomizationMoney(artist.customization_min_price_cents)}` : 'To be discussed'}</strong>
        {artist.bio && <Body>{artist.bio}</Body>}
        {artist.portfolio_images?.length > 0 && <Portfolio>{artist.portfolio_images.map((url) => <PortfolioImage key={url} src={url} alt={`${artist.brand_name} portfolio`} />)}</Portfolio>}
        <Muted>{artist.review_count ? `${Number(artist.review_average || 0).toFixed(1)} ★ · ${artist.review_count} reviews` : 'No reviews yet'}</Muted>
        <Actions><ButtonLink to={`/custom-painting/${requestId}/send/${artist.shop_id}`} state={{ artist }} onClick={() => trackPaintingCustomization('painting_artist_selected', { request_id: Number(requestId), shop_id: artist.shop_id })}>Choose this artist</ButtonLink><ButtonLink to={`/artist-shop/${artistItemsAPI.buildShopRef(artist.shop_id)}`} $secondary>View gallery</ButtonLink></Actions>
      </Card>)}</Grid>
    </Container></Page>
  );
}

export function BuyerCustomizationSendPage() {
  const { requestId, selectedShopId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [artist, setArtist] = useState(state?.artist || null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    Promise.all([
      customizationsAPI.getBuyerRequest(requestId),
      artist ? Promise.resolve(null) : customizationsAPI.getRecommendations(requestId),
    ]).then(([value, recommendations]) => {
      setRequest(value);
      if (!artist) setArtist((recommendations?.results || []).find((entry) => String(entry.shop_id) === String(selectedShopId)) || null);
    }).catch((err) => setError(apiMessage(err))).finally(() => setLoading(false));
  }, [artist, requestId, selectedShopId]);
  const send = async () => {
    setSending(true); setError('');
    try {
      const result = await customizationsAPI.sendBuyerRequest(requestId, Number(selectedShopId));
      trackPaintingCustomization('painting_request_sent', {
        request_id: result.id,
        shop_id: Number(selectedShopId),
        catalog_item_id: result.catalog_item,
        negotiation_id: result.negotiation_id,
        state: result.state,
      });
      toast.success(`Request sent to ${artist.brand_name}.`);
      navigate(`/customization-requests/${result.id}`, { replace: true });
    } catch (err) { setError(apiMessage(err, 'We couldn’t send your request. Try again.')); }
    finally { setSending(false); }
  };
  if (loading) return <Page><LoadingSpinner text="Loading request…" /></Page>;
  if (!request || !artist) return <Page><Container><ErrorBanner>{error || 'This artist is no longer accepting customization requests.'}</ErrorBanner><ButtonLink to={`/custom-painting/${requestId}/artists`}>Choose another artist</ButtonLink></Container></Page>;
  return (
    <Page><Container>
      <Back to={`/custom-painting/${requestId}/artists`}>← Choose another artist</Back>
      <Title>Send your request?</Title>
      <Card><Heading style={{ marginTop: 0 }}>{artist.brand_name}</Heading><strong>{artist.customization_min_price_cents ? `Starting from ${formatCustomizationMoney(artist.customization_min_price_cents)}` : 'To be discussed'}</strong></Card>
      <Notice style={{ marginTop: '1rem' }}>{request.painting_brief.width_cm} × {request.painting_brief.height_cm} cm · {briefDirection(request.painting_brief.creative_direction)} · {formatCustomizationMoney(request.budget_min_cents)} – {formatCustomizationMoney(request.budget_max_cents)} · {(request.references || []).length} references</Notice>
      <Body style={{ marginTop: '1rem' }}>Your full painting brief and reference images will be shared with {artist.brand_name}. You can ask questions in chat after sending.</Body>
      <Body>{artist.customization_min_price_cents ? `This artist starts at ${formatCustomizationMoney(artist.customization_min_price_cents)}. Their quote may differ based on your brief.` : 'Price: To be discussed.'}</Body>
      <Muted>Sending a request is free and doesn’t commit you to buy.</Muted>
      {error && <ErrorBanner>{error}</ErrorBanner>}
      <Actions><Button onClick={send} disabled={sending}>{sending ? 'Sending…' : 'Send request'}</Button><ButtonLink to={`/custom-painting/${requestId}/artists`} $secondary>Choose another artist</ButtonLink></Actions>
    </Container></Page>
  );
}

export function BuyerCustomizationRequestsPage() {
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState('All');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { setRequests((await customizationsAPI.listBuyerRequests()).results); }
    catch (err) { setError(apiMessage(err, 'We couldn’t load your requests.')); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const shown = useMemo(() => requests.filter((request) => {
    if (filter === 'Needs action') return ['draft', 'submitted', 'quoted'].includes(request.state);
    if (filter === 'In progress') return ['sent', 'accepted'].includes(request.state);
    if (filter === 'Closed') return ['converted', 'cancelled', 'expired'].includes(request.state);
    return true;
  }), [filter, requests]);
  const path = (request) => {
    if (request.kind === 'boutique') {
      if (request.state === 'draft') return `/custom-order/${request.id}/step/1`;
      if (request.state === 'accepted') return '/cart';
      if (request.state === 'converted') return '/orders';
      return `/customization-requests/${request.id}`;
    }
    if (request.kind === 'designer') {
      if (request.state === 'draft') return `/custom-order/designer/${request.id}/step/1`;
      if (request.state === 'accepted') return '/cart';
      if (request.state === 'converted') return '/orders';
      return `/customization-requests/${request.id}`;
    }
    return request.state === 'draft'
      ? `/custom-painting/${request.id}/step/${JSON.parse(localStorage.getItem(META_KEY(request.id)) || '{}').lastStep || 1}`
      : request.state === 'submitted'
        ? `/custom-painting/${request.id}/artists`
        : request.state === 'accepted'
          ? '/cart'
          : request.state === 'converted'
            ? '/orders'
            : `/customization-requests/${request.id}`;
  };
  return (
    <Page><Container $wide>
      <Back to="/artists">← Back</Back><Title>My customization requests</Title>
      <Chips>{['All', 'Needs action', 'In progress', 'Closed'].map((value) => <Chip type="button" key={value} $active={filter === value} onClick={() => setFilter(value)}>{value}</Chip>)}</Chips>
      {loading && <LoadingSpinner text="Loading requests…" />}
      {error && <><ErrorBanner>{error}</ErrorBanner><Button onClick={load}>Try again</Button></>}
      {!loading && !error && !shown.length && <Card style={{ marginTop: '1rem' }}><Heading>{requests.length ? 'No requests in this status.' : 'No customization requests yet'}</Heading>{!requests.length && <Muted>Start a custom painting from Galleries, or a custom order from Boutiques or Designers.</Muted>}</Card>}
      <Grid style={{ marginTop: '1rem' }}>{shown.map((request) => <Card key={request.id}>
        <Badge>{kindLabel[request.kind] || request.kind}</Badge>
        <Heading style={{ marginTop: '0.65rem' }}>
          {(request.kind === 'boutique' || request.kind === 'designer')
            ? (request.offering?.title || request.selected_shop?.brand_name || 'Custom order')
            : shopName(request)}
        </Heading>
        <Body>
          {(request.kind === 'boutique' || request.kind === 'designer')
            ? (request.selected_shop?.brand_name || (request.kind === 'designer' ? 'Designer' : 'Boutique'))
            : `${request.painting_brief?.width_cm || '—'} × ${request.painting_brief?.height_cm || '—'} cm`}
        </Body>
        <Badge>{statusLabel[request.state] || request.state}</Badge>
        <Muted>Updated {new Date(request.updated_at).toLocaleDateString()}</Muted>
        <Actions><ButtonLink to={path(request)}>{request.state === 'draft' ? 'Continue brief' : request.state === 'submitted' && request.kind === 'painting' ? 'View artists' : request.state === 'quoted' ? 'Review quote' : request.state === 'accepted' ? 'Go to cart' : 'View request'}</ButtonLink></Actions>
      </Card>)}</Grid>
    </Container></Page>
  );
}

export function BuyerCustomizationDetailPage() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { setRequest(await customizationsAPI.getBuyerRequest(requestId)); }
    catch (err) { setError(apiMessage(err)); }
    finally { setLoading(false); }
  }, [requestId]);
  useEffect(() => { load(); }, [load]);
  const mutate = async (kind) => {
    const prompts = {
      accept: ['Accept this quote?', `The painting price is ${formatCustomizationMoney(quoteFor(request)?.amount_cents)}. It will be added to your cart; existing checkout fees, if any, are shown at checkout.`],
      decline: ['Decline this quote?', 'The artist may send a revised quote after you continue the conversation.'],
      cancel: ['Cancel this request?', 'This can’t be undone.'],
    };
    if (!window.confirm(`${prompts[kind][0]}\n\n${prompts[kind][1]}`)) return;
    setBusy(kind); setError('');
    try {
      const updated = kind === 'accept' ? await customizationsAPI.acceptQuote(request.id) : kind === 'decline' ? await customizationsAPI.declineQuote(request.id) : await customizationsAPI.cancelBuyerRequest(request.id);
      setRequest(updated);
      if (kind === 'accept' || kind === 'decline') {
        const activeQuote = quoteFor(request);
        trackPaintingCustomization(
          kind === 'accept' ? 'painting_quote_accepted' : 'painting_quote_declined',
          {
            request_id: request.id,
            shop_id: shopId(request),
            quote_id: activeQuote?.id,
            amount_cents: activeQuote?.amount_cents,
            state: updated.state,
          },
        );
      }
      if (kind === 'accept') {
        await dispatch(fetchCart());
        navigate('/cart');
      }
    } catch (err) {
      setError(apiMessage(err));
      if ([409, 410].includes(err.response?.status)) { toast.info('This request has been updated.'); load(); }
    } finally { setBusy(''); }
  };
  if (loading) return <Page><LoadingSpinner text="Loading request…" /></Page>;
  if (!request) return <Page><Container><ErrorBanner>{error || 'This request isn’t available.'}</ErrorBanner><Actions><Button onClick={load}>Try again</Button><ButtonLink to="/customization-requests" $secondary>Back to requests</ButtonLink></Actions></Container></Page>;
  const quote = quoteFor(request);
  const chatPath = request.negotiation_id ? `/negotiations/${negotiationsAPI.buildNegotiationRef(request.negotiation_id)}` : null;
  const isOfferingBased = request.kind === 'boutique' || request.kind === 'designer';
  const isBoutique = request.kind === 'boutique';
  const isDesigner = request.kind === 'designer';
  const statusCopy = request.state === 'submitted' && !isOfferingBased ? 'Choose an artist to send your request.'
    : request.state === 'sent' ? `Waiting for ${shopName(request)} to send a quote. They have 7 days to respond.`
      : request.state === 'quoted' ? 'Your quote is ready.'
        : request.state === 'accepted' ? `Quote accepted. Your ${isOfferingBased ? 'custom order' : 'painting'} is in your cart.`
          : request.state === 'converted' ? 'Order placed.'
            : request.state === 'expired' ? (quote ? 'This quote expired.' : 'This request expired before a quote was sent.')
              : request.state === 'cancelled' ? 'This request was cancelled.' : '';
  return (
    <Page><Container>
      <Back to="/customization-requests">← Back to list</Back>
      <Title>{isOfferingBased ? 'Custom order request' : 'Custom painting request'}</Title>
      <Badge>{statusLabel[request.state] || request.state}</Badge>
      {statusCopy && <Notice style={{ marginTop: '1rem' }}>{statusCopy}</Notice>}
      {request.state === 'draft' && isBoutique && (
        <Actions><ButtonLink to={`/custom-order/${request.id}/step/1`}>Continue brief</ButtonLink></Actions>
      )}
      {request.state === 'draft' && isDesigner && (
        <Actions><ButtonLink to={`/custom-order/designer/${request.id}/step/1`}>Continue brief</ButtonLink></Actions>
      )}
      {request.state === 'submitted' && !isOfferingBased && <Actions><ButtonLink to={`/custom-painting/${request.id}/artists`}>View artists</ButtonLink></Actions>}
      {shopId(request) && (
        <Card style={{ marginTop: '1rem' }}>
          <Heading style={{ marginTop: 0 }}>{shopName(request)}</Heading>
          {!isOfferingBased ? (
            <ButtonLink to={`/artist-shop/${artistItemsAPI.buildShopRef(shopId(request))}`} $secondary>View gallery</ButtonLink>
          ) : null}
        </Card>
      )}
      {quote && (
        <Card style={{ marginTop: '1rem' }}>
          <Heading style={{ marginTop: 0 }}>{isOfferingBased ? 'Order price' : 'Painting price'}</Heading>
          <Title>{formatCustomizationMoney(quote.amount_cents)}</Title>
          <Body><strong>Estimated delivery:</strong> {quote.delivery_days} days</Body>
          {quote.message && <><strong>{isBoutique ? 'Boutique note' : isDesigner ? 'Designer note' : 'Artist’s note'}</strong><Body>{quote.message}</Body></>}
          <Muted>Checkout fees, if any, are shown at checkout.</Muted>
        </Card>
      )}
      {request.state === 'quoted' && <Actions><Button onClick={() => mutate('accept')} disabled={Boolean(busy)}>{busy === 'accept' ? 'Accepting…' : 'Accept quote and add to cart'}</Button><Button $secondary onClick={() => mutate('decline')} disabled={Boolean(busy)}>{busy === 'decline' ? 'Declining…' : 'Decline quote'}</Button></Actions>}
      {request.state === 'accepted' && <Actions><ButtonLink to="/cart">Go to cart</ButtonLink></Actions>}
      {request.state === 'converted' && <Actions><ButtonLink to="/orders">View order</ButtonLink></Actions>}
      {chatPath && ['sent', 'quoted', 'accepted', 'converted'].includes(request.state) && <Actions><ButtonLink to={chatPath} $secondary>Ask a question</ButtonLink></Actions>}
      <Heading>{isOfferingBased ? 'Order brief' : 'Painting brief'}</Heading>
      {isOfferingBased
        ? (
          <BoutiqueBriefView
            brief={request.boutique_brief}
            offering={request.offering}
            references={request.references}
          />
        )
        : <PaintingBriefView request={request} />}
      {['draft', 'submitted', 'sent', 'quoted'].includes(request.state) && <Actions><Button $secondary onClick={() => mutate('cancel')} disabled={Boolean(busy)}>{busy === 'cancel' ? 'Cancelling…' : 'Cancel request'}</Button></Actions>}
      {error && <ErrorBanner>{error}</ErrorBanner>}
    </Container></Page>
  );
}
